import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FiBriefcase, FiTool, FiActivity, FiUsers, FiPlus, FiX } from 'react-icons/fi';
import { getSkillCategories, getCommonActivities, mapInputToSkills } from '../../../services/skillMapping';

interface DiscoveryStepProps {
    data: {
        enjoyDoing: string[];
        haveDone: string[];
        wantNow: string[];
    };
    onUpdate: (data: any) => void;
    onNext: () => void;
}

const iconMap: Record<string, any> = {
    FiBriefcase,
    FiTool,
    FiActivity,
    FiUsers
};

const DiscoveryStep: React.FC<DiscoveryStepProps> = ({ data, onUpdate, onNext }) => {
    const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
    const [customInput, setCustomInput] = useState('');
    const [activeQuestion, setActiveQuestion] = useState<'enjoy' | 'done' | 'want'>('enjoy');

    const categories = getSkillCategories();

    const handleCategorySelect = (categoryId: string) => {
        setSelectedCategory(categoryId);
    };

    const handleActivityClick = (activity: string) => {
        const field = activeQuestion === 'enjoy' ? 'enjoyDoing' :
            activeQuestion === 'done' ? 'haveDone' : 'wantNow';

        const current = data[field] || [];

        if (current.includes(activity)) {
            // Remove if already selected
            onUpdate({
                ...data,
                [field]: current.filter((a: string) => a !== activity)
            });
        } else {
            // Add to selection
            onUpdate({
                ...data,
                [field]: [...current, activity]
            });
        }
    };

    const handleAddCustom = () => {
        if (!customInput.trim()) return;

        const field = activeQuestion === 'enjoy' ? 'enjoyDoing' :
            activeQuestion === 'done' ? 'haveDone' : 'wantNow';

        const current = data[field] || [];
        onUpdate({
            ...data,
            [field]: [...current, customInput.trim()]
        });
        setCustomInput('');
    };

    const handleRemoveItem = (field: 'enjoyDoing' | 'haveDone' | 'wantNow', item: string) => {
        onUpdate({
            ...data,
            [field]: data[field].filter((a: string) => a !== item)
        });
    };

    const canProceed = data.enjoyDoing.length > 0 || data.haveDone.length > 0 || data.wantNow.length > 0;

    return (
        <div className="space-y-6">
            {/* Question Tabs */}
            <div className="flex gap-2 border-b border-white/10">
                <button
                    onClick={() => setActiveQuestion('enjoy')}
                    className={`px-4 py-3 font-medium transition-all relative ${activeQuestion === 'enjoy' ? 'text-premium-gold' : 'text-white/60 hover:text-white'
                        }`}
                >
                    What I Enjoy
                    {activeQuestion === 'enjoy' && (
                        <motion.div
                            layoutId="activeQuestion"
                            className="absolute bottom-0 left-0 right-0 h-0.5 bg-premium-gold"
                        />
                    )}
                </button>
                <button
                    onClick={() => setActiveQuestion('done')}
                    className={`px-4 py-3 font-medium transition-all relative ${activeQuestion === 'done' ? 'text-premium-gold' : 'text-white/60 hover:text-white'
                        }`}
                >
                    What I've Done
                    {activeQuestion === 'done' && (
                        <motion.div
                            layoutId="activeQuestion"
                            className="absolute bottom-0 left-0 right-0 h-0.5 bg-premium-gold"
                        />
                    )}
                </button>
                <button
                    onClick={() => setActiveQuestion('want')}
                    className={`px-4 py-3 font-medium transition-all relative ${activeQuestion === 'want' ? 'text-premium-gold' : 'text-white/60 hover:text-white'
                        }`}
                >
                    What I Want
                    {activeQuestion === 'want' && (
                        <motion.div
                            layoutId="activeQuestion"
                            className="absolute bottom-0 left-0 right-0 h-0.5 bg-premium-gold"
                        />
                    )}
                </button>
            </div>

            {/* Category Selection */}
            <div>
                <h3 className="text-lg font-bold text-white mb-3">Choose a category to explore:</h3>
                <div className="grid grid-cols-2 gap-3">
                    {categories.map((category) => {
                        const IconComponent = iconMap[category.icon];
                        return (
                            <motion.button
                                key={category.id}
                                onClick={() => handleCategorySelect(category.id)}
                                whileHover={{ scale: 1.02 }}
                                whileTap={{ scale: 0.98 }}
                                className={`p-4 rounded-xl border-2 transition-all text-left ${selectedCategory === category.id
                                        ? 'border-premium-gold bg-premium-gold/10'
                                        : 'border-white/10 bg-white/5 hover:border-white/20'
                                    }`}
                            >
                                <div className="flex items-start gap-3">
                                    <div
                                        className="p-2 rounded-lg"
                                        style={{ backgroundColor: `${category.color}20` }}
                                    >
                                        <IconComponent size={20} style={{ color: category.color }} />
                                    </div>
                                    <div className="flex-1">
                                        <div className="font-bold text-white text-sm mb-1">
                                            {category.name}
                                        </div>
                                        <div className="text-xs text-white/60">
                                            {category.description}
                                        </div>
                                    </div>
                                </div>
                            </motion.button>
                        );
                    })}
                </div>
            </div>

            {/* Activity Selection */}
            {selectedCategory && (
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="space-y-3"
                >
                    <h3 className="text-sm font-bold text-premium-gold">
                        Select activities you're interested in:
                    </h3>
                    <div className="flex flex-wrap gap-2">
                        {getCommonActivities(selectedCategory).map((activity) => {
                            const field = activeQuestion === 'enjoy' ? 'enjoyDoing' :
                                activeQuestion === 'done' ? 'haveDone' : 'wantNow';
                            const isSelected = data[field]?.includes(activity);

                            return (
                                <motion.button
                                    key={activity}
                                    onClick={() => handleActivityClick(activity)}
                                    whileHover={{ scale: 1.05 }}
                                    whileTap={{ scale: 0.95 }}
                                    className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${isSelected
                                            ? 'bg-premium-gold text-[#1a1510]'
                                            : 'bg-white/5 text-white/80 hover:bg-white/10 border border-white/10'
                                        }`}
                                >
                                    {activity}
                                </motion.button>
                            );
                        })}
                    </div>

                    {/* Custom Input */}
                    <div className="flex gap-2">
                        <input
                            type="text"
                            value={customInput}
                            onChange={(e) => setCustomInput(e.target.value)}
                            onKeyPress={(e) => e.key === 'Enter' && handleAddCustom()}
                            placeholder="Or type your own..."
                            className="flex-1 bg-black/30 border border-white/10 rounded-lg px-4 py-2 text-white placeholder-white/30 focus:outline-none focus:border-premium-gold/50"
                        />
                        <button
                            onClick={handleAddCustom}
                            className="px-4 py-2 bg-premium-gold/20 text-premium-gold rounded-lg hover:bg-premium-gold/30 transition-all border border-premium-gold/30"
                        >
                            <FiPlus size={20} />
                        </button>
                    </div>
                </motion.div>
            )}

            {/* Selected Items Display */}
            {(data.enjoyDoing.length > 0 || data.haveDone.length > 0 || data.wantNow.length > 0) && (
                <div className="space-y-3 p-4 bg-white/5 rounded-xl border border-white/10">
                    <h3 className="text-sm font-bold text-white">Your Selections:</h3>

                    {data.enjoyDoing.length > 0 && (
                        <div>
                            <p className="text-xs text-premium-gold/80 mb-2">I Enjoy:</p>
                            <div className="flex flex-wrap gap-2">
                                {data.enjoyDoing.map((item) => (
                                    <span
                                        key={item}
                                        className="px-3 py-1 bg-green-500/20 text-green-300 rounded-full text-xs flex items-center gap-2 border border-green-500/30"
                                    >
                                        {item}
                                        <button
                                            onClick={() => handleRemoveItem('enjoyDoing', item)}
                                            className="hover:text-white transition-colors"
                                        >
                                            <FiX size={14} />
                                        </button>
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}

                    {data.haveDone.length > 0 && (
                        <div>
                            <p className="text-xs text-premium-gold/80 mb-2">I've Done:</p>
                            <div className="flex flex-wrap gap-2">
                                {data.haveDone.map((item) => (
                                    <span
                                        key={item}
                                        className="px-3 py-1 bg-blue-500/20 text-blue-300 rounded-full text-xs flex items-center gap-2 border border-blue-500/30"
                                    >
                                        {item}
                                        <button
                                            onClick={() => handleRemoveItem('haveDone', item)}
                                            className="hover:text-white transition-colors"
                                        >
                                            <FiX size={14} />
                                        </button>
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}

                    {data.wantNow.length > 0 && (
                        <div>
                            <p className="text-xs text-premium-gold/80 mb-2">I Want:</p>
                            <div className="flex flex-wrap gap-2">
                                {data.wantNow.map((item) => (
                                    <span
                                        key={item}
                                        className="px-3 py-1 bg-purple-500/20 text-purple-300 rounded-full text-xs flex items-center gap-2 border border-purple-500/30"
                                    >
                                        {item}
                                        <button
                                            onClick={() => handleRemoveItem('wantNow', item)}
                                            className="hover:text-white transition-colors"
                                        >
                                            <FiX size={14} />
                                        </button>
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Next Button */}
            <div className="flex justify-end">
                <button
                    onClick={onNext}
                    disabled={!canProceed}
                    className={`px-8 py-3 rounded-lg font-bold transition-all ${canProceed
                            ? 'bg-gradient-to-r from-premium-gold-dark to-premium-gold text-[#1a1510] hover:shadow-[0_0_20px_rgba(212,175,55,0.4)] transform hover:scale-105'
                            : 'bg-white/10 text-white/40 cursor-not-allowed'
                        }`}
                >
                    Continue to Skills Confirmation
                </button>
            </div>
        </div>
    );
};

export default DiscoveryStep;
